import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  FerrisWheel,
  FileText,
  Gift,
  Loader2,
  ShoppingCart,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CustomerInfoForm } from '@/components/pos/CustomerInfoForm';
import { CouponField } from '@/components/pos/CouponField';
import { DiscountList } from '@/components/pos/DiscountList';
import { QuoteDiscountRows } from '@/components/pos/QuoteDiscountRows';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { StepIndicator } from '@/components/pos/StepIndicator';
import { PaymentDialog } from '@/components/pos/PaymentDialog';
import { PaidScreen } from '@/components/pos/PaidScreen';
import { BlockedDateBanner } from '@/components/pos/BlockedDateBanner';
import {
  useCartStore,
  cartSelectors,
  lineTicketItems,
  lineTicketTotal,
  selectTotal,
} from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';
import { useHotkeys } from '@/hooks/useHotkeys';
import { usePaymentMethods, useVisitDateBlock } from '@/hooks/queries';
import { useCreateSale } from '@/hooks/mutations';
import { useOnline } from '@/hooks/useOnline';
import { useCartQuote } from '@/hooks/useCartQuote';
import { getPromoCandidates } from '@/api/discount.api';
import { buildSalePayload } from '@/lib/buildSalePayload';
import { blockedDateMessage, visitDateErrorMessage } from '@/lib/visitDateErrors';
import { focErrorMessage } from '@/lib/focDocument';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateSaleResult } from '@/types/sale';
import type { ApiError } from '@/types/api';

export function CheckoutPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    lines,
    bundleLines,
    customer,
    visitDate,
    promo,
    mode,
    focDocument,
  } = useCartStore(
    useShallow((s) => ({
      lines: s.lines,
      bundleLines: s.bundleLines,
      customer: s.customer,
      visitDate: s.visitDate,
      promo: s.promo,
      mode: s.mode,
      focDocument: s.focDocument,
    })),
  );
  const isFocCart = mode === 'foc';
  // Only an FOC cart carries its document; a regular sale never sends the key.
  const focDocumentId = isFocCart ? (focDocument?.id ?? null) : null;
  const subtotal = useCartStore(cartSelectors.subtotal);
  const savings = useCartStore(cartSelectors.couponSavings);
  const localTotal = useCartStore(selectTotal);
  const cartCount = lines.length + bundleLines.length;

  // Itemize attraction children in the summary (main lines show main-only price).
  const attractionSummary = useMemo(() => {
    const map = new Map<string, { name: string; quantity: number; total: number }>();
    const fold = (a: { productId: string; name: string; price: number; quantity: number }) => {
      const cur = map.get(a.productId) ?? { name: a.name, quantity: 0, total: 0 };
      cur.quantity += a.quantity;
      cur.total += a.price * a.quantity;
      map.set(a.productId, cur);
    };
    for (const l of lines) for (const seat of l.seats) seat.addOns.forEach(fold);
    // Extra attractions attached to bundles are itemized here too.
    for (const b of bundleLines) b.extraAddOns.forEach(fold);
    return Array.from(map.values());
  }, [lines, bundleLines]);

  const [customerValid, setCustomerValid] = useState(Boolean(customer.name.trim()));
  const [payOpen, setPayOpen] = useState(false);
  const [paid, setPaid] = useState<CreateSaleResult | null>(null);
  // Visit-date rejection from POST /sale (blocked / out of range). Sticks for
  // this visit to the page: the date can only be changed back on the catalog.
  const [saleDateError, setSaleDateError] = useState<string | null>(null);
  // FOC rejection from POST /sale (document missing / used / not found, cart
  // not all-FOC). Sticks too: the fix is on the document step or the catalog.
  const [saleFocError, setSaleFocError] = useState<string | null>(null);

  // Empty-cart guard (back to the flow's own catalog). An FOC cart without its
  // supporting document goes back to attach one.
  useEffect(() => {
    if (paid) return;
    if (cartCount === 0) navigate(isFocCart ? '/foc' : '/', { replace: true });
    else if (isFocCart && !focDocumentId) navigate('/foc/document', { replace: true });
  }, [cartCount, paid, isFocCart, focDocumentId, navigate]);

  const eligibleCart = useMemo(() => {
    // Add-on attractions are products too, and discounts that match them take their price off at
    // checkout, so the promo preview must see them or its "Hemat Rp…" comes out too low.
    const addOns = new Map<string, { productId: string; quantity: number; price: number }>();
    const fold = (a: { productId: string; price: number; quantity: number }) => {
      const cur = addOns.get(a.productId) ?? { productId: a.productId, quantity: 0, price: a.price };
      cur.quantity += a.quantity;
      addOns.set(a.productId, cur);
    };
    for (const l of lines) for (const seat of l.seats) seat.addOns.forEach(fold);
    for (const b of bundleLines) b.extraAddOns.forEach(fold);
    return {
      items: [
        // Seats of one line can sit in differently priced slots (a time-of-day rate).
        ...lines.flatMap((l) =>
          lineTicketItems(l).map(({ productId, quantity, price }) => ({
            productId,
            quantity,
            price,
          })),
        ),
        ...addOns.values(),
      ],
      totalAmount: subtotal,
    };
  }, [lines, bundleLines, subtotal]);

  // ─── Live BE-authoritative total (POST /sale/quote) ────────────────────────
  // A neutral placeholder payment method satisfies the validator; price is
  // method-independent (rate plan → slot override → event surge → discounts),
  // so the shown total equals the eventual charge. Falls back to the client
  // estimate on any error.
  const { data: methods = [] } = usePaymentMethods();
  // One quote author for the cart, Details and here (same cache entry). All
  // slots are picked by now, so no stand-in is used; the placeholder method's
  // reasoning lives on `pickQuoteMethod`.
  const { query: quoteQuery, quote, quoteMethod } = useCartQuote({ enabled: !paid });

  // Promo-scheme context (v.1.0.22). Scheme-gated discounts (GROUP / IDENTITAS /
  // PAYMENT / channel) only appear as eligible when the matching context is
  // supplied. `idCardNumber` is deliberately omitted — it's PII and not needed
  // for eligibility. Note: PAYMENT-gated promos surface against the quote's
  // placeholder method, so the shown gateway may differ from the one actually
  // chosen at the payment dialog.
  const eligibleContext = useMemo(
    () => ({
      groupType: promo.groupType,
      sourceChannel: promo.sourceChannel,
      paymentGateway: quoteMethod?.paymentGateway,
      paymentMethodId: quoteMethod?.id,
      identity: promo.identity
        ? {
            idCardRegion: promo.identity.idCardRegion ?? undefined,
            birthMonth: promo.identity.birthMonth,
            ktpVerified: promo.identity.ktpVerified,
          }
        : null,
      // Document-OCR scan → the preview endpoint runs the same document-gate
      // sub-validator, so a scanned-document promo surfaces as eligible here.
      // The authoritative gate is still the sale-time `documentScanToken`.
      document: promo.documentScan
        ? { documentType: promo.documentScan.documentType, fields: promo.documentScan.fields }
        : null,
      // A promo limited to visit dates must be listed for the day being sold.
      visitDate,
    }),
    [promo, quoteMethod, visitDate],
  );

  const { data: candidates = [] } = useQuery({
    queryKey: ['promoCandidates', eligibleCart.totalAmount, cartCount, eligibleContext],
    queryFn: () => getPromoCandidates(eligibleCart, eligibleContext),
    enabled: cartCount > 0 && !paid,
  });
  const total = quote?.total ?? localTotal;

  // Blocked visit date — from the public feed, the quote, or a rejected sale.
  // Any of them locks payment (other quote errors still fall back to the local
  // estimate, as before).
  const visitBlock = useVisitDateBlock(visitDate);
  const quoteDateError = quoteQuery.isError ? visitDateErrorMessage(quoteQuery.error) : null;
  const dateError =
    saleDateError ??
    quoteDateError ??
    (visitBlock ? blockedDateMessage(visitBlock.reason, visitDate) : null);

  // FOC rejection — from a refused sale, or already from the quote (the quote
  // runs the same document/cart checks), so a used document is caught before
  // the cashier presses pay.
  const quoteFocError = quoteQuery.isError ? focErrorMessage(quoteQuery.error) : null;
  const focError = isFocCart ? (saleFocError ?? quoteFocError) : null;
  const blockingError = dateError ?? focError;

  // ─── Free-of-charge confirm (total Rp 0) ───────────────────────────────────
  // An FOC cart with nothing paid on it skips the payment dialog: the sale is
  // posted straight away on the cash tender with amountTendered 0. A paid
  // attraction add-on makes the total non-zero, and that goes to payment.
  const isFreeConfirm = isFocCart && total === 0;
  // The offline tender, found the way the chooser finds it: getPaymentMethods
  // normalises the gateway-less "Cash" row to `cash`.
  const cashMethod = useMemo(() => methods.find((m) => m.paymentGateway === 'cash'), [methods]);
  const { mutate: createSale, isPending: confirming } = useCreateSale();
  const online = useOnline();
  // One idempotency key per checkout session, reused across retries — the same
  // handling as the cash panel, so a flaky-network retry can't create two sales.
  const idemKeyRef = useRef<string>('');
  if (!idemKeyRef.current) idemKeyRef.current = crypto.randomUUID();

  const canPay =
    customerValid &&
    cartCount > 0 &&
    !blockingError &&
    // A free confirm waits for the authoritative quote: it posts no tender, so
    // it must not fire on a stale Rp 0 estimate.
    !(isFreeConfirm && (confirming || quoteQuery.isFetching));

  function handleFocRejected(message: string) {
    setSaleFocError(message);
    void queryClient.invalidateQueries({ queryKey: ['saleQuote'] });
  }

  function confirmFree() {
    if (!online) {
      toast.error('Koneksi terputus — transaksi tidak dapat diproses. Sambungkan kembali internet.');
      return;
    }
    if (!cashMethod) {
      toast.error('Metode pembayaran tunai tidak tersedia untuk konfirmasi tiket FOC.');
      return;
    }
    const cart = useCartStore.getState();
    const payload = buildSalePayload({
      lines: cart.lines,
      bundleLines: cart.bundleLines,
      customer: cart.customer,
      visitDate: cart.visitDate,
      couponCode: cart.couponCode,
      discountIds: cart.discountIds,
      paymentMethodId: cashMethod.id,
      paymentGateway: 'cash',
      amountTendered: 0,
      groupType: cart.promo.groupType,
      sourceChannel: cart.promo.sourceChannel,
      identity: cart.promo.identity,
      documentScanToken: cart.promo.documentScanToken,
      focDocumentId: cart.focDocument?.id ?? null,
    });
    createSale(
      { payload, idempotencyKey: idemKeyRef.current },
      {
        onSuccess: (result) => setPaid(result),
        onError: (err) => {
          const apiErr = err as ApiError;
          // Same order as the payment dialog: visit date, then FOC, then a
          // capacity 409 (FOC_DOCUMENT_ALREADY_USED is a 409 too).
          const dateMsg = visitDateErrorMessage(apiErr);
          if (dateMsg) {
            toast.error(dateMsg);
            setSaleDateError(dateMsg);
            void queryClient.invalidateQueries({ queryKey: ['blockedDates'] });
            void queryClient.invalidateQueries({ queryKey: ['saleQuote'] });
            return;
          }
          const focMsg = focErrorMessage(apiErr);
          if (focMsg) {
            toast.error(focMsg);
            handleFocRejected(focMsg);
            return;
          }
          toast.error(apiErr?.message ?? 'Gagal membuat transaksi.');
          const blocked =
            apiErr?.code === 409 || (apiErr?.payload as { blocked?: boolean })?.blocked === true;
          if (blocked) navigate('/foc');
        },
      },
    );
  }

  function openPay() {
    if (blockingError) {
      toast.error(blockingError);
      return;
    }
    if (!canPay) return;
    if (isFreeConfirm) confirmFree();
    else setPayOpen(true);
  }

  useHotkeys({
    f8: openPay,
    f9: openPay,
    escape: () => !payOpen && !paid && !confirming && navigate('/details'),
  });

  if (paid) {
    return <PaidScreen result={paid} />;
  }

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/details')}>
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Button>
        <StepIndicator flow={mode} current={mode === 'foc' ? 3 : 2} />
      </div>

      {visitBlock ? (
        <BlockedDateBanner block={visitBlock} visitDate={visitDate} className="mb-4" />
      ) : dateError ? (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-xl border-2 border-warning/40 bg-warning/5 px-4 py-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <p className="font-medium text-ink-900">{dateError}</p>
        </div>
      ) : focError ? (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-start gap-3 rounded-xl border-2 border-warning/40 bg-warning/5 px-4 py-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <p className="min-w-0 flex-1 font-medium text-ink-900">{focError}</p>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => navigate('/foc/document')}
          >
            <FileText className="h-4 w-4" /> Ke dokumen FOC
          </Button>
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-5">
        {/* Left — forms. `min-h-0` lets this grid item shrink below its content
            so the inner ScrollArea gets a bounded height and actually scrolls. */}
        <div className="min-h-0 lg:col-span-3">
          <ScrollArea className="h-full pr-3">
            <div className="space-y-5 pb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informasi pelanggan</CardTitle>
                </CardHeader>
                <CardContent>
                  <CustomerInfoForm onValidityChange={setCustomerValid} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Kupon & promo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <CouponField />
                  <DiscountList candidates={candidates} />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </div>

        {/* Right — summary */}
        <div className="min-h-0 lg:col-span-2">
          <Card className="flex h-full flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" /> Ringkasan pesanan
                {isFocCart && (
                  <Badge variant="gold" className="ml-auto gap-1">
                    <Gift className="h-3 w-3" aria-hidden="true" /> FOC
                  </Badge>
                )}
              </CardTitle>
              {isFocCart && focDocument && (
                <p className="flex min-w-0 items-center gap-1.5 text-xs text-ink-600">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-crimson-700" aria-hidden="true" />
                  <span className="shrink-0 text-ink-400">Dokumen:</span>
                  <span className="truncate font-medium text-ink-900" title={focDocument.name}>
                    {focDocument.name}
                  </span>
                </p>
              )}
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="flex-1">
                <ul className="space-y-2 pr-2">
                  {bundleLines.map((b) => (
                    <li key={b.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">
                          📦 {b.name}
                        </span>
                        <span className="tnum text-xs text-ink-400">paket · × {b.quantity}</span>
                      </span>
                      <MoneyValue value={b.unitPrice * b.quantity} className="text-sm" />
                    </li>
                  ))}
                  {lines.map((l) => (
                    <li key={l.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">{l.name}</span>
                        <span className="tnum text-xs text-ink-400">× {l.seats.length}</span>
                      </span>
                      <MoneyValue value={lineTicketTotal(l)} className="text-sm" />
                    </li>
                  ))}
                  {attractionSummary.map((a) => (
                    <li key={a.name} className="flex justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <FerrisWheel className="h-3.5 w-3.5 shrink-0 text-gold-600" />
                        <span className="min-w-0">
                          <span className="block truncate text-ink-700">{a.name}</span>
                          <span className="tnum text-xs text-ink-400">× {a.quantity}</span>
                        </span>
                      </span>
                      <MoneyValue value={a.total} className="text-sm" />
                    </li>
                  ))}
                </ul>
              </ScrollArea>

              <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-ink-600">
                  <span>Subtotal</span>
                  <MoneyValue value={quote?.subtotal ?? subtotal} />
                </div>
                <QuoteDiscountRows quote={quote} fallbackSavings={savings} />
                <Separator className="my-2" />
                <div className="flex items-end justify-between">
                  <span className="font-semibold uppercase tracking-wide text-ink-600">Total</span>
                  <span className="text-right">
                    <MoneyValue value={total} emphasis className="text-3xl" />
                    {quoteQuery.isFetching && (
                      <span className="block text-[11px] font-normal text-ink-400">menghitung…</span>
                    )}
                  </span>
                </div>
              </div>

              <Button
                size="lg"
                variant="gold"
                className="mt-4 h-16 w-full text-xl"
                disabled={!canPay}
                onClick={openPay}
              >
                {confirming ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : isFreeConfirm ? (
                  <>
                    Konfirmasi &amp; Cetak <Gift className="h-6 w-6" />
                  </>
                ) : (
                  <>
                    Bayar <ArrowRight className="h-6 w-6" />
                  </>
                )}
                {!confirming && (
                  <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F8</kbd>
                )}
              </Button>
              {dateError ? (
                <p className="mt-2 text-center text-xs text-warning">
                  Tanggal kunjungan tidak dapat dijual — kembali ke katalog dan pilih tanggal lain.
                </p>
              ) : focError ? (
                <p className="mt-2 text-center text-xs text-warning">
                  Perbaiki dokumen atau keranjang FOC untuk melanjutkan.
                </p>
              ) : !customerValid ? (
                <p className="mt-2 text-center text-xs text-warning">
                  Lengkapi data pelanggan untuk melanjutkan.
                </p>
              ) : isFreeConfirm ? (
                <p className="mt-2 text-center text-xs text-ink-400">
                  Tiket FOC gratis — tanpa pembayaran, langsung dicetak.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        total={total}
        onPaid={(result) => {
          setPayOpen(false);
          setPaid(result);
        }}
        onCapacityBlocked={() => navigate(isFocCart ? '/foc' : '/')}
        onFocRejected={handleFocRejected}
        onVisitDateRejected={(message) => {
          setSaleDateError(message);
          // The feed may lag a block created moments ago — refresh it (and the
          // quote) so the catalog and banner catch up.
          void queryClient.invalidateQueries({ queryKey: ['blockedDates'] });
          void queryClient.invalidateQueries({ queryKey: ['saleQuote'] });
        }}
      />
    </div>
  );
}
